import { useState } from 'react'
import { ChevronLeft, ChevronRight, RotateCcw, Layers } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Progress } from '@/components/ui/progress'
import FormRenderer from '../../components/FormRenderer'
import { activePages } from '../../lib/form'
import type { Answers, FormDefinition } from '../../types'

/** The draft as respondents see it. Answers live only in this component. */
export default function PreviewTab({ def }: { def: FormDefinition }) {
    const [answers, setAnswers] = useState<Answers>({})
    const [step, setStep] = useState(0)
    const pages = activePages(def, answers)
    const i = Math.min(step, Math.max(0, pages.length - 1))
    const page = pages[i]

    if (!page) {
        return (
            <Empty className="border border-dashed">
                <EmptyHeader>
                    <EmptyMedia variant="icon"><Layers /></EmptyMedia>
                    <EmptyTitle>Nothing to preview</EmptyTitle>
                    <EmptyDescription>Add a page with fields on the Design tab, or check the page conditions.</EmptyDescription>
                </EmptyHeader>
            </Empty>
        )
    }
    return (
        <div className="mx-auto flex max-w-3xl flex-col gap-3">
            <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
                <span>Preview only. Answers are not saved.</span>
                <Button variant="ghost" size="sm" onClick={() => { setAnswers({}); setStep(0) }}><RotateCcw data-icon="inline-start" /> Reset</Button>
            </div>
            <Progress value={((i + 1) / pages.length) * 100} aria-label={`Page ${i + 1} of ${pages.length}`} />
            <Card>
                <CardHeader>
                    <CardDescription>Page {i + 1} of {pages.length}</CardDescription>
                    <CardTitle>{page.title}</CardTitle>
                    {page.description && <CardDescription>{page.description}</CardDescription>}
                </CardHeader>
                <CardContent>
                    <FormRenderer page={page} answers={answers} onChange={(k, v) => setAnswers((a) => ({ ...a, [k]: v }))} />
                </CardContent>
                <CardFooter className="justify-between">
                    <Button variant="outline" disabled={i === 0} onClick={() => setStep(i - 1)}><ChevronLeft data-icon="inline-start" /> Back</Button>
                    <Button disabled={i >= pages.length - 1} onClick={() => setStep(i + 1)}>Next <ChevronRight data-icon="inline-end" /></Button>
                </CardFooter>
            </Card>
        </div>
    )
}
